import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import DOMPurify from 'dompurify';
import supabase from '../supabase/supabase';
import Navbar from '../components/landing/Navbar';
import Footer from '../components/landing/Footer';
import SeoStructuredData from '../components/seo/SeoStructuredData';
import { useAuth } from '../contexts/AuthContext';
import { appUrl, publicUrl } from '../utils/routing';
import { track } from '../utils/analytics';
import { applySeoHead, buildBreadcrumbSchema } from '../utils/seoHead';
import {
  buildLegacyTemplateSeoPath,
  buildTemplateIntentPath,
  buildTemplateSeoPath,
  getTemplateSeoTaxonomy,
  templateMatchesPublicPath,
} from '../utils/templateSeo';
import { buildModelSeoProfile, getHubSeoProfile, rankRelatedTemplates } from '../utils/modelSeoContent';

const PROFESSIONAL_NOTICE = 'O Laudando é uma ferramenta para profissionais de saúde elaborarem e emitirem seus próprios laudos. Não solicita, intermedeia ou fornece laudos ao público.';
const MODALITY_ORDER = ['ultrassonografia', 'raio-x', 'ecg', 'tomografia', 'ressonancia', 'veterinaria', 'outros'];

const TemplateCard = ({ template }) => {
  const taxonomy = getTemplateSeoTaxonomy(template);
  return (
    <Link to={taxonomy.path} className="group rounded-card border border-line bg-paper p-5 shadow-card transition-all hover:-translate-y-0.5 hover:border-navy/35">
      <span className="mb-4 block text-[10px] font-semibold uppercase tracking-widest text-muted">{taxonomy.modalityLabel}</span>
      <h2 className="font-display text-lg font-bold leading-snug text-ink transition-colors group-hover:text-navy">{template.name}</h2>
      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted">{template.description || 'Modelo profissional editável para agilizar a elaboração do laudo.'}</p>
      <div className="mt-5 flex items-center gap-2 border-t border-line pt-4 text-sm font-semibold text-navy">Ver modelo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></div>
    </Link>
  );
};

const AtomicAnswer = ({ title, children }) => (
  <section className="rounded-card border border-line bg-paper p-6">
    <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
    <div className="mt-3 text-sm leading-7 text-muted md:text-base">{children}</div>
  </section>
);

const sectionLabelsFromHtml = (html = '') => {
  const labels = [];
  const regex = /<(?:strong|h[1-3])[^>]*>(.*?)<\/(?:strong|h[1-3])>/gi;
  let match;
  while ((match = regex.exec(html)) !== null && labels.length < 8) {
    const text = String(match[1] || '').replace(/<[^>]+>/g, '').trim();
    if (text && !labels.includes(text)) labels.push(text);
  }
  return labels;
};

const formatReviewDate = (value) => {
  if (!value) return null;
  try { return new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return null; }
};

const faqForTemplate = (template, taxonomy) => {
  const veterinary = template.category === 'veterinario';
  const professional = veterinary ? 'médico-veterinário' : 'profissional habilitado';
  return [
    {
      q: `O que deve constar em um ${template.name.toLowerCase()}?`,
      a: `A estrutura deve refletir o exame efetivamente realizado. Este modelo organiza seções recorrentes para que o ${professional} registre apenas informações pertinentes ao exame e produza uma conclusão coerente com o documento.`,
    },
    {
      q: 'Posso copiar este modelo e emitir sem revisar?',
      a: 'Não. O conteúdo é um ponto de partida editável. Antes da emissão, o profissional deve revisar integralmente o texto, remover trechos não aplicáveis e inserir somente informações sustentadas pelo exame realizado.',
    },
    {
      q: `Este modelo serve para todos os casos de ${taxonomy.modalityLabel.toLowerCase()}?`,
      a: 'Não necessariamente. Técnica, protocolo, medidas e estrutura variam conforme o exame e o contexto profissional. O modelo deve ser adaptado ao caso real.',
    },
  ];
};

export default function PublicTemplates() {
  const location = useLocation();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  const isCatalog = pathname === '/modelos-laudo';

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('templates')
        .select('id,name,description,content,specialty,author_name,category,usage_count,rating_avg,rating_count,seo_slug,seo_title,seo_description,seo_status,seo_sanitized,is_public,updated_at')
        .eq('is_public', true)
        .eq('seo_status', 'approved')
        .eq('seo_sanitized', true)
        .order('usage_count', { ascending: false });
      if (active) {
        if (!error) setTemplates(data || []);
        setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const template = useMemo(() => isCatalog ? null : templates.find((item) => templateMatchesPublicPath(item, pathname)) || null, [isCatalog, pathname, templates]);

  const hub = useMemo(() => {
    if (isCatalog || template) return null;
    const grouped = templates.reduce((acc, item) => {
      const taxonomy = getTemplateSeoTaxonomy(item);
      if (!acc.has(taxonomy.hubPath)) acc.set(taxonomy.hubPath, taxonomy);
      return acc;
    }, new Map());
    return grouped.get(pathname) || null;
  }, [isCatalog, pathname, template, templates]);

  const hubTemplates = useMemo(() => hub ? templates.filter((item) => getTemplateSeoTaxonomy(item).modality === hub.modality) : [], [hub, templates]);
  const related = useMemo(() => template ? rankRelatedTemplates(template, templates) : [], [template, templates]);

  const filtered = useMemo(() => {
    const source = hub ? hubTemplates : templates;
    const needle = query.trim().toLowerCase();
    if (!needle) return source;
    return source.filter((item) => `${item.name} ${item.description || ''} ${item.specialty || ''}`.toLowerCase().includes(needle));
  }, [query, templates, hub, hubTemplates]);

  const modalityHubs = useMemo(() => {
    const map = new Map();
    templates.forEach((item) => {
      const taxonomy = getTemplateSeoTaxonomy(item);
      if (taxonomy.hubPath !== '/modelos-laudo' && !map.has(taxonomy.modality)) map.set(taxonomy.modality, taxonomy);
    });
    return MODALITY_ORDER.map((key) => map.get(key)).filter(Boolean);
  }, [templates]);

  useEffect(() => {
    if (template) {
      const taxonomy = getTemplateSeoTaxonomy(template);
      const profile = buildModelSeoProfile(template);
      applySeoHead({ title: profile.title, description: profile.description, canonical: publicUrl(taxonomy.path), robots: 'index,follow' });
      track('template_preview_viewed', { template_id: template.id, template_slug: template.seo_slug || '', template_modality: taxonomy.modality, acquisition_surface: 'public_template' });
    } else if (hub) {
      const hubProfile = getHubSeoProfile(hub.modality);
      applySeoHead({
        title: `Modelos de laudo de ${hub.modalityLabel} | Laudando`,
        description: hubProfile.intro,
        canonical: publicUrl(hub.hubPath),
        robots: 'index,follow',
      });
    } else if (isCatalog) {
      applySeoHead({
        title: 'Modelos de laudos médicos e veterinários | Laudando',
        description: 'Biblioteca de modelos de laudos médicos e veterinários com exemplos editáveis, estrutura profissional e acesso direto ao editor.',
        canonical: publicUrl('/modelos-laudo'),
        robots: 'index,follow',
      });
    }
  }, [template?.id, hub?.hubPath, isCatalog]);

  if (loading) return <div className="min-h-screen bg-paper-alt"><Navbar /><div className="flex min-h-[60vh] items-center justify-center text-muted">Carregando modelos…</div><Footer /></div>;

  if (template && pathname === buildLegacyTemplateSeoPath(template)) return <Navigate to={buildTemplateSeoPath(template)} replace />;

  if (!isCatalog && !template && !hub) {
    return (
      <div className="min-h-screen bg-paper-alt">
        <Navbar />
        <main className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="max-w-lg rounded-card border border-line bg-paper p-8 text-center shadow-card">
            <h1 className="font-display text-2xl font-bold text-ink">Modelo não encontrado</h1>
            <p className="mt-3 text-muted">Este modelo pode ter sido renomeado ou retirado do catálogo público.</p>
            <Link to="/modelos-laudo" className="btn-primary mt-6 inline-flex">Ver todos os modelos</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!template) {
    const veterinaryHub = hub?.modality === 'veterinaria';
    const hubProfile = hub ? getHubSeoProfile(hub.modality) : null;
    const title = hub ? (veterinaryHub ? 'Modelos de laudos veterinários' : `Modelos de laudo de ${hub.modalityLabel}`) : 'Modelos de laudos médicos e veterinários';
    const lead = hub ? hubProfile.intro : 'Encontre o exame, consulte uma estrutura real de laudo e abra o modelo diretamente no editor. A biblioteca reúne modelos médicos e veterinários em silos próprios para uso por profissionais habilitados.';
    const canonical = publicUrl(hub?.hubPath || '/modelos-laudo');

    return (
      <div className="min-h-screen bg-paper-alt">
        <SeoStructuredData data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: title,
          description: lead,
          url: canonical,
          isPartOf: { '@type': 'WebSite', name: 'Laudando', url: publicUrl('/') },
          mainEntity: { '@type': 'ItemList', numberOfItems: filtered.length, itemListElement: filtered.slice(0, 30).map((item, index) => ({ '@type': 'ListItem', position: index + 1, name: item.name, url: publicUrl(getTemplateSeoTaxonomy(item).path) })) },
        }} />
        <Navbar />
        <main>
          <section className="mx-auto max-w-5xl px-4 pb-10 pt-16 text-center sm:px-6 lg:px-8">
            <h1 className="font-display text-4xl font-bold tracking-tight text-ink md:text-6xl">{title}</h1>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-muted">{lead}</p>
            {hubProfile?.reference_path && <Link to={hubProfile.reference_path} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:underline">Como estruturar laudos de {hub.modalityLabel.toLowerCase()}</Link>}
          </section>

          <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
            {!hub && modalityHubs.length > 0 && (
              <nav aria-label="Categorias de laudos" className="mb-8 flex flex-wrap justify-center gap-2">
                {modalityHubs.map((item) => <Link key={item.modality} to={item.hubPath} className="rounded-full border border-line bg-paper px-4 py-2 text-sm font-semibold text-ink transition-colors hover:border-navy/40 hover:text-navy">{item.modalityLabel}</Link>)}
              </nav>
            )}
            {hub && <div className="mb-5"><Link to="/modelos-laudo" className="text-sm font-semibold text-navy hover:underline">← Todos os modelos</Link></div>}
            <div className="mb-8 rounded-card border border-line bg-paper p-4 shadow-card md:p-5">
              <div className="relative"><Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted" /><input value={query} onChange={(event) => setQuery(event.target.value)} className="input-field pl-12 text-base" placeholder={veterinaryHub ? 'Buscar modelo veterinário…' : 'Qual laudo você vai emitir? Ex.: tórax, ECG, ultrassonografia…'} aria-label="Buscar modelos de laudo" /></div>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">{filtered.map((item) => <TemplateCard key={item.id} template={item} />)}</div>
            {filtered.length === 0 && <div className="py-14 text-center text-muted">Nenhum modelo encontrado para essa busca.</div>}
          </section>

          <section className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-4 pb-16 sm:px-6 md:grid-cols-3 lg:px-8">
            <AtomicAnswer title="O que é esta biblioteca?">Um catálogo público de estruturas de laudos destinadas a profissionais habilitados, com páginas canônicas por exame e modalidade.</AtomicAnswer>
            <AtomicAnswer title="Como os modelos devem ser usados?">Como ponto de partida. O profissional abre o modelo no editor, adapta o texto ao exame realizado e revisa o documento antes de emitir.</AtomicAnswer>
            <AtomicAnswer title={veterinaryHub ? 'Este hub pertence ao silo veterinário?' : 'Quem pode emitir estes laudos?'}>{veterinaryHub ? 'Sim. O conteúdo veterinário permanece separado dos hubs médicos humanos e é destinado a médicos-veterinários habilitados.' : 'A emissão é responsabilidade do profissional habilitado. O Laudando organiza o fluxo e não fornece laudos ao público.'}</AtomicAnswer>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const taxonomy = getTemplateSeoTaxonomy(template);
  const profile = buildModelSeoProfile(template);
  const intentPath = buildTemplateIntentPath(template.id);
  const ctaHref = user?.id ? appUrl(intentPath) : appUrl(`/login?redirect=${encodeURIComponent(intentPath)}`);
  const sanitizedContent = DOMPurify.sanitize(template.content || '');
  const reviewDate = formatReviewDate(template.updated_at);
  const parsedStructure = sectionLabelsFromHtml(template.content);
  const structureLabels = parsedStructure.length >= 2 ? parsedStructure : profile.structure_labels;
  const faq = faqForTemplate(template, taxonomy);
  const canonical = publicUrl(taxonomy.path);
  const breadcrumbs = [
    { name: 'Modelos de laudos', path: '/modelos-laudo' },
    { name: taxonomy.modalityLabel, path: taxonomy.hubPath },
    { name: profile.h1, path: taxonomy.path },
  ];

  const handleEmitClick = () => track('template_emit_clicked', {
    template_id: template.id,
    template_slug: template.seo_slug || '',
    template_modality: taxonomy.modality,
    auth_state: user?.id ? 'authenticated' : 'anonymous',
    acquisition_surface: 'public_template',
  });

  return (
    <div className="min-h-screen bg-paper-alt">
      <SeoStructuredData data={{
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            name: profile.h1,
            headline: profile.title,
            description: profile.description,
            url: canonical,
            dateModified: template.updated_at || undefined,
            isPartOf: { '@type': 'WebSite', name: 'Laudando', url: publicUrl('/') },
            audience: { '@type': 'Audience', audienceType: profile.is_veterinary ? 'Médicos-veterinários' : 'Profissionais de saúde' },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((item, index) => ({ '@type': 'ListItem', position: index + 1, name: item.name, item: publicUrl(item.path) })),
          },
          {
            '@type': 'FAQPage',
            mainEntity: faq.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
          },
        ],
      }} />
      <Navbar />
      <main>
        <section className="mx-auto max-w-5xl px-4 pb-7 pt-12 sm:px-6 lg:px-8">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-muted" aria-label="Breadcrumb"><Link to="/modelos-laudo" className="hover:text-navy">Modelos</Link><span>/</span><Link to={taxonomy.hubPath} className="hover:text-navy">{taxonomy.modalityLabel}</Link></nav>
          {profile.is_veterinary && <div className="mb-4"><span className="rounded-full border border-selo/20 bg-selo-tint px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-selo">Veterinário</span></div>}
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight text-ink md:text-5xl">{profile.h1}</h1>
          <div className="mt-6 max-w-4xl rounded-card border border-selo/20 bg-selo-tint p-5 md:p-6"><p className="mb-2 text-sm font-semibold text-selo">Em resumo</p><p className="text-base leading-8 text-ink md:text-lg">{profile.direct_answer}</p></div>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted">{template.author_name && <span>Responsável editorial: <strong className="text-ink">{template.author_name}</strong></span>}{reviewDate && <span>Revisado em {reviewDate}</span>}</div>
          <a href={ctaHref} onClick={handleEmitClick} className="btn-convert mt-7 inline-flex justify-center">Usar este modelo no Laudando <ArrowRight className="h-4 w-4" /></a>
        </section>

        <section className="mx-auto max-w-5xl px-4 pb-10 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-card border border-line bg-paper shadow-card">
            <div className="flex items-center justify-between gap-4 border-b border-line bg-paper-alt/60 px-6 py-4"><p className="text-sm font-semibold text-ink">Exemplo de laudo</p><span className="text-[10px] font-semibold uppercase tracking-widest text-muted">Revise antes de emitir</span></div>
            <div className="p-6 md:p-10"><div className="report-content prose prose-slate max-w-none leading-7 text-ink" dangerouslySetInnerHTML={{ __html: sanitizedContent }} /></div>
          </div>
        </section>

        <section className="mx-auto grid max-w-5xl gap-5 px-4 py-8 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="rounded-card border border-line bg-paper p-7">
            <p className="mb-1 text-sm font-semibold text-muted">Estrutura recomendada</p><h2 className="font-display text-2xl font-bold text-ink">Estrutura deste laudo</h2>
            <p className="mt-3 leading-7 text-muted">Use a estrutura como roteiro de revisão, não como conjunto universal de campos.</p>
            <ul className="mt-5 space-y-3">{structureLabels.map((label) => <li key={label} className="rounded-card border border-line bg-paper-alt px-4 py-3 text-sm text-ink">{label}</li>)}</ul>
          </div>
          <div className="rounded-card border border-line bg-paper p-7">
            <h2 className="font-display text-2xl font-bold text-ink">O que revisar antes de emitir</h2>
            <ul className="mt-5 list-disc space-y-3 pl-5">{profile.review_checklist.map((item) => <li key={item} className="text-sm leading-6 text-ink">{item}</li>)}</ul>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 pb-10 sm:px-6 lg:px-8">
          <div className="rounded-card border border-line bg-paper p-7">
            <h2 className="font-display text-2xl font-bold text-ink">Como adaptar este modelo</h2>
            <p className="mt-3 leading-7 text-muted">{profile.adaptation_guidance}</p>
            {profile.reference_path && <Link to={profile.reference_path} className="mt-5 inline-flex items-center gap-2 font-semibold text-navy hover:underline">Ver referência de estrutura para {profile.modality_label.toLowerCase()}</Link>}
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 pb-12 sm:px-6 lg:px-8">
          <div className="rounded-card bg-navy p-6 text-white shadow-card md:flex md:items-center md:justify-between md:gap-8 md:p-8">
            <div><h2 className="font-display text-2xl font-bold">Abra exatamente este modelo no editor.</h2><p className="mt-2 max-w-2xl text-white/75">O conteúdo chega pré-selecionado para você adaptar ao exame e revisar antes da emissão.</p></div>
            <a href={ctaHref} onClick={handleEmitClick} className="btn-convert mt-5 inline-flex shrink-0 justify-center whitespace-nowrap md:mt-0">Emitir este laudo agora <ArrowRight className="h-4 w-4" /></a>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 pb-14 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-ink">Perguntas frequentes</h2>
          <div className="mt-6 space-y-3">{faq.map((item) => <details key={item.q} className="group rounded-card border border-line bg-paper p-5"><summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-ink">{item.q}<span className="text-xl font-normal text-navy transition-transform group-open:rotate-45">+</span></summary><p className="mt-3 pr-8 text-sm leading-7 text-muted md:text-base">{item.a}</p></details>)}</div>
        </section>

        {related.length > 0 && (
          <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
            <h2 className="mb-6 font-display text-2xl font-bold text-ink">Modelos relacionados</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">{related.map((item) => <TemplateCard key={item.id} template={item} />)}</div>
          </section>
        )}

        <section className="mx-auto max-w-4xl space-y-4 px-4 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-card border border-line bg-paper p-6"><div><h2 className="font-semibold text-ink">Responsabilidade profissional</h2><p className="mt-1 text-sm leading-relaxed text-muted">Esta página apresenta uma estrutura de modelo aprovada para consulta profissional. O exemplo não substitui protocolo institucional, diretriz aplicável, correlação clínica ou interpretação do exame.</p></div></div>
          <div className="rounded-card border border-line bg-paper p-6"><div><h2 className="font-semibold text-ink">O que o Laudando não faz</h2><p className="mt-1 text-sm leading-relaxed text-muted">{PROFESSIONAL_NOTICE}</p></div></div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
